import React from 'react';
import type { InputV1Props } from '.';
import { animationTransition } from '../../utils';
import type { HTMLInputProps } from '../../common/props';
import type { ThemeStyledProps } from '@shared/joykit/packages/core/common/styledComponents';
import { styled, css } from '@shared/joykit/packages/core/common/styledComponents';

const InputComponent = React.forwardRef<HTMLInputElement, InputV1Props & HTMLInputProps>(
  (
    {
      autoFocus,
      children,
      error,
      focusedBorderColor,
      ghost,
      iconLeft,
      iconRight,
      inputRef,
      minimal,
      suppressFocusRing,
      flex,
      order,
      margin,
      marginX,
      marginY,
      marginTop,
      marginBottom,
      marginLeft,
      marginRight,
      ...props
    },
    ref,
  ) => {
    return (
      <input
        {...props}
        ref={ref}
      />
    );
  },
);

const minimalStyle = (props: ThemeStyledProps<InputV1Props>) => {
  return css`
    border: none;
  `;
};
const ghostStyle = (props: ThemeStyledProps<InputV1Props>) => {
  return css`
    background-color: transparent;
    padding: 0;
  `;
};

/**
 * The width of the resting hairline. The container insets its ring by this much so the ring lands
 * on top of the hairline instead of beside it -- change one and the other follows.
 */
export const FIELD_BORDER_WIDTH = '1px';

export const StyledInput = styled(InputComponent)`
  /* The resting hairline never changes colour -- hover, focus and invalid are carried by the
     container's ring. See doc/field-focus-consistency/PLAN.md. */
  border: ${FIELD_BORDER_WIDTH} solid ${props => props.theme.colors.inputBorder};
  border-radius: ${props => props.theme.radii.field};
  display: block;
  min-height: 48px;
  outline: none;
  padding: 0 16px;
  width: 100%;
  ${props => props.theme.typography.variants.body1};
  transition: ${animationTransition('border-color', 'background-color', 'color')};

  ${props => props.minimal && minimalStyle(props)}
  ${props => props.ghost && ghostStyle(props)}

  :read-only {
    cursor: default;
  }

  :disabled {
    background-color: ${props => !props.minimal && props.theme.colors.buttonDisabledFill};
    color: ${props => props.theme.colors.typeDisabledDark};
    cursor: not-allowed;
  }
`;
